import { type FieldMetadata, getFormProps, type useForm } from '@conform-to/react';
import { EnvelopeIcon, LockClosedIcon } from '@heroicons/react/16/solid';
import { Form, Link, useNavigation } from '@remix-run/react';
import { Button } from 'flowbite-react';
import { HoneypotInputs } from 'remix-utils/honeypot/react';
import { z } from 'zod';
import { FormCheckboxField } from '#common/components/catalyst-ui-extensions/FormCheckboxField.js';
import { FormInputFieldWithIcon } from '#common/components/catalyst-ui-extensions/FormInputFieldWithIcon.js';
import { Spinner } from '#common/components/Spinner.js';
import { useEditableTranslation } from '#common/i18n/edit-mode.tsx';

export const loginSchema = z.object({
    email: z.string().email(),
    password: z.string(),
    redirectTo: z.string().optional(),
    remember: z.boolean().optional()
});

export type TLoginFormData = z.infer<typeof loginSchema>;

interface LoginProps {
    form: ReturnType<typeof useForm<TLoginFormData>>[0];
    fields: {
        email: FieldMetadata<string>;
        password: FieldMetadata<string>;
        redirectTo?: FieldMetadata<string>;
        remember: FieldMetadata<boolean | undefined>;
    };
    redirectTo: string;
    createAccountUrl?: string;
}

export function Login({ form, fields, redirectTo, createAccountUrl }: LoginProps) {
    const { t, rawT } = useEditableTranslation('login');
    const navigation = useNavigation();
    const isSubmitting = navigation.state === 'submitting';

    return (
        <Form method='post' className='space-y-6' {...getFormProps(form)} noValidate reloadDocument>
            <HoneypotInputs />
            <FormInputFieldWithIcon
                field={fields.email}
                icon={<EnvelopeIcon className='w-5 h-5 text-gray-500' />}
                placeholder={rawT('email')}
                type='email'
                id='username'
                autoComplete='username'
            />
            <FormInputFieldWithIcon
                field={fields.password}
                icon={<LockClosedIcon className='w-5 h-5 text-gray-500' />}
                placeholder={rawT('password')}
                type='password'
                id='password'
                autoComplete='current-password'
            />
            <FormCheckboxField
                field={fields.remember}
                label={'Remember Me'}
                inputClassName='w-[16px] h-[16px] cursor-pointer'
            />
            <input type='hidden' name='redirectTo' value={redirectTo} />
            <Button type='submit' disabled={isSubmitting} className='w-full h-10 cursor-pointer'>
                {isSubmitting ? <Spinner size='sm' /> : t('login')}
            </Button>
            <div className='flex justify-between items-center'>
                <Link
                    className='dark:text-crispyblue-500 text-crispyblue-700 text-sm font-medium underline cursor-pointer'
                    to={'/forgot-password'}
                >
                    {t('forgotPassword')}
                </Link>
                {createAccountUrl && (
                    <Link
                        className='dark:text-crispyblue-500 text-crispyblue-700 text-sm font-medium underline cursor-pointer'
                        to={createAccountUrl}
                    >
                        {t('createAnAccount')}
                    </Link>
                )}
            </div>
        </Form>
    );
}
